import React from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, ExternalLink } from "lucide-react";

export default function AdminPayouts() {
    const [items, setItems] = React.useState([]);

    const load = React.useCallback(async () => {
        // Pull all admin commissions; backend already attaches everything we need
        const { data } = await api.get("/admin/commissions");
        setItems(data || []);
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const markPaidOut = async (id) => {
        if (!window.confirm("Confirm: you have wired the funds to the creator and want to mark this payout as paid out?")) return;
        try {
            await api.post(`/admin/commissions/${id}/mark-paid-out`);
            toast.success("Marked paid out");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    const funded = items.filter((c) => c.payout_status === "funded");
    const paidOut = items.filter((c) => c.payout_status === "paid_out");

    return (
        <div className="space-y-6" data-testid="admin-payouts">
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-coral" />
                    <h3 className="font-display text-lg font-semibold">Pending creator payouts ({funded.length})</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    These commissions are <span className="font-semibold">funded</span> by the brand. Castline holds the cash. Wire it to the creator (using the bank/PayPal/Stripe info from their Payouts profile), then mark paid out below.
                </p>
                <div className="card-ecom overflow-hidden">
                    {funded.length === 0 ? (
                        <div className="p-10 text-center text-sm text-slate-500">
                            No pending payouts right now.
                        </div>
                    ) : (
                        funded.map((c, i) => (
                            <div
                                key={c.id}
                                className={`p-5 flex flex-wrap items-start justify-between gap-3 ${
                                    i !== funded.length - 1 ? "border-b hairline" : ""
                                }`}
                                data-testid={`pending-payout-${c.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="font-display font-semibold text-slate-900">
                                        {c.title}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        Brand: <span className="font-semibold">{c.brand_name}</span>
                                        {" · "}Creator: <span className="font-semibold">{c.creator_name || c.creator_id}</span>
                                        {c.funded_at && <> · funded {new Date(c.funded_at).toLocaleDateString()}</>}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className="font-display text-2xl font-semibold text-coral">
                                        ${(c.funded_amount || c.budget || 0).toFixed(2)}
                                    </div>
                                    <button
                                        onClick={() => markPaidOut(c.id)}
                                        className="btn-pill-primary inline-flex items-center mt-2 text-xs"
                                        data-testid={`mark-paid-${c.id}`}
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark paid out
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div>
                <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-4 h-4 text-lime-ink" />
                    <h3 className="font-display text-lg font-semibold">Paid out ({paidOut.length})</h3>
                </div>
                <div className="card-ecom overflow-hidden">
                    {paidOut.length === 0 ? (
                        <div className="p-10 text-center text-sm text-slate-500">
                            None yet — your first payout will show up here.
                        </div>
                    ) : (
                        paidOut.map((c, i) => (
                            <div
                                key={c.id}
                                className={`p-4 flex items-center justify-between text-sm ${
                                    i !== paidOut.length - 1 ? "border-b hairline" : ""
                                }`}
                            >
                                <div>
                                    <div className="font-semibold text-slate-900">{c.title}</div>
                                    <div className="text-xs text-slate-500">
                                        {c.brand_name} → {c.creator_name || c.creator_id}
                                        {c.paid_out_at && <> · {new Date(c.paid_out_at).toLocaleDateString()}</>}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="chip chip-success">paid out</span>
                                    <div className="text-coral font-semibold mt-1">
                                        ${(c.funded_amount || c.budget || 0).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <a
                href="https://dashboard.stripe.com/test/payments"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill-ghost inline-flex items-center text-sm"
                data-testid="open-stripe"
            >
                <ExternalLink className="w-4 h-4 mr-2" /> Open Stripe dashboard
            </a>
        </div>
    );
}
